import { GripVertical, RotateCcw, Settings } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { ScrollArea, ScrollAreaScrollbar, ScrollAreaViewport } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { Switch } from '@/components/ui/switch';

export interface ColumnDefinition {
  id: string;
  label: string;
  visible: boolean;
  locked?: boolean;
  order: number;
}

interface ColumnConfigDialogProps {
  columns: ColumnDefinition[];
  onApply: (columns: ColumnDefinition[]) => void;
  onReset: () => void;
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export function ColumnConfigDialog({
  columns,
  onApply,
  onReset,
  trigger,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
}: ColumnConfigDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const isControlled = controlledOpen !== undefined;
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? (v: boolean) => controlledOnOpenChange?.(v) : setInternalOpen;
  const [localColumns, setLocalColumns] = useState<ColumnDefinition[]>([]);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);

  useEffect(() => {
    if (open) {
      setLocalColumns([...columns].sort((a, b) => a.order - b.order));
      setDraggedIndex(null);
      setDragOverIndex(null);
    }
  }, [open, columns]);

  const handleToggleColumn = (columnId: string) => {
    setLocalColumns((prev) => prev.map((col) => (col.id === columnId ? { ...col, visible: !col.visible } : col)));
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (draggedIndex !== null && dragOverIndex !== null && draggedIndex !== dragOverIndex) {
      const newColumns = [...localColumns];
      const [draggedColumn] = newColumns.splice(draggedIndex, 1);
      newColumns.splice(dragOverIndex, 0, draggedColumn);
      newColumns.forEach((col, idx) => {
        col.order = idx;
      });
      setLocalColumns(newColumns);
    }
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleReset = () => {
    onReset();
    setOpen(false);
  };

  const handleApply = () => {
    const reordered = localColumns.map((col, idx) => ({
      ...col,
      order: idx,
    }));
    onApply(reordered);
    setOpen(false);
  };

  const visibleCount = localColumns.filter((col) => col.visible).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!isControlled && (
        <DialogTrigger asChild>
          {trigger ?? (
            <Button variant="outline" size="icon" title="Configure Columns">
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </DialogTrigger>
      )}
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Configure Table Columns</DialogTitle>
          <DialogDescription>Show, hide, and reorder columns. Drag columns to reorder them.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-muted-foreground">
              {visibleCount} of {localColumns.length} columns visible
            </div>
            <Button variant="ghost" size="sm" onClick={handleReset} className="gap-2">
              <RotateCcw className="h-4 w-4" />
              Reset to Default
            </Button>
          </div>

          <Separator />

          <ScrollArea className="h-[400px]">
            <ScrollAreaViewport className="h-full pr-4">
              <div className="space-y-2">
                {localColumns.map((column, index) => (
                  <div
                    key={column.id}
                    draggable={!column.locked}
                    onDragStart={() => handleDragStart(index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDragEnd={handleDragEnd}
                    className={`
                      flex items-center justify-between p-3 rounded-lg border bg-card
                      ${!column.locked ? 'cursor-move hover:bg-accent' : 'cursor-default'}
                      ${draggedIndex === index ? 'opacity-50' : ''}
                      ${dragOverIndex === index ? 'border-primary' : ''}
                      transition-all
                    `}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      {!column.locked && <GripVertical className="h-4 w-4 text-muted-foreground" />}
                      <Label
                        htmlFor={`column-${column.id}`}
                        className={`flex-1 ${column.locked ? 'ml-7' : ''} ${
                          !column.visible ? 'text-muted-foreground' : ''
                        }`}
                      >
                        {column.label}
                        {column.locked && <span className="ml-2 text-xs text-muted-foreground">(Required)</span>}
                      </Label>
                    </div>
                    <Switch
                      id={`column-${column.id}`}
                      checked={column.visible}
                      onCheckedChange={() => handleToggleColumn(column.id)}
                      disabled={column.locked}
                    />
                  </div>
                ))}
              </div>
            </ScrollAreaViewport>
            <ScrollAreaScrollbar orientation="vertical" />
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button onClick={handleApply}>Apply Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
